import { z } from 'zod';
import { isoDateSchema, objectIdSchema } from './common.js';
import { journeySummarySchema } from './content.js';
/**
 * The journey engine's contract.
 *
 * Both state machines are PERSISTED, never inferred from which screen the user is on, so
 * they travel on the wire as explicit values rather than as something the app derives.
 */
/* ───────────────────────── state ───────────────────────── */
/**
 * A user's journey.
 *
 *   STARTED → ACTIVE → COMPLETED → ARCHIVED, with PAUSED off ACTIVE.
 *
 * STARTED means started but nothing finished yet; the first completed day flips it to
 * ACTIVE. The distinction is what lets "started but never opened again" be told apart from
 * "genuinely in progress" without guessing from timestamps.
 *
 * AVAILABLE is in the brief's diagram but is not a stored value: a journey the user has
 * not started has no row at all, which is the same thing and cannot drift.
 */
export const userJourneyStatusSchema = z.enum([
    'STARTED',
    'ACTIVE',
    'COMPLETED',
    'PAUSED',
    'ARCHIVED',
]);
/** A day. LOCKED → AVAILABLE → IN_PROGRESS → COMPLETED. */
export const dayStatusSchema = z.enum(['LOCKED', 'AVAILABLE', 'IN_PROGRESS', 'COMPLETED']);
/**
 * A story is complete at 80% LISTENED — not on play, and not on reaching the end.
 *
 * The divisor is accumulated listening, not the furthest position reached, so dragging the
 * scrubber to the end does not complete a day. Evaluated server-side on every heartbeat;
 * the client never decides this.
 */
export const COMPLETION_THRESHOLD = 0.8;
/* ───────────────────────── journey detail ───────────────────────── */
export const journeyDaySchema = z.object({
    dayNumber: z.number().int().min(1),
    title: z.string(),
    status: dayStatusSchema,
    /** The activity to open. STORY only in the MVP. */
    activityId: objectIdSchema.nullable(),
    storyId: objectIdSchema.nullable(),
    storyTitle: z.string().nullable(),
    durationSec: z.number().int(),
    /** Where to drop the playhead when this day is reopened. */
    positionSec: z.number().int(),
    /** Accumulated listening, which is what the 80% rule divides by. */
    listenedSec: z.number().int(),
    /** Signed, short-lived, and null until a banner is uploaded for this day's story. */
    bannerUrl: z.string().nullable(),
    completedAt: isoDateSchema.nullable(),
    /** The heart on the player (added 2026-10-18). A mark on the user's own day row. */
    favourite: z.boolean(),
});
/** The heart's body, shared by days and collection episodes. */
export const favouriteBodySchema = z.object({ favourite: z.boolean() });
export const favouriteActivityResponseSchema = z.object({
    activityId: objectIdSchema,
    favourite: z.boolean(),
});
/** The journey detail screen: Day 1 ✓ · Day 2 ✓ · Day 3 CURRENT · Day 4 🔒 */
export const userJourneyDetailSchema = z.object({
    journey: journeySummarySchema,
    status: userJourneyStatusSchema,
    progressPct: z.number().int().min(0).max(100),
    /** The lowest day that is not COMPLETED — what "continue" opens. */
    currentDay: z.number().int().min(1),
    startedAt: isoDateSchema,
    completedAt: isoDateSchema.nullable(),
    days: z.array(journeyDaySchema),
    /** Whether this journey's feedback has already been given. Feedback is once per journey. */
    feedbackGiven: z.boolean(),
});
/** A row in My Journeys. Deliberately lighter than the detail — no day array. */
export const userJourneySummarySchema = z.object({
    journey: journeySummarySchema,
    status: userJourneyStatusSchema,
    progressPct: z.number().int().min(0).max(100),
    currentDay: z.number().int().min(1),
    totalDays: z.number().int(),
    completedDays: z.number().int(),
    startedAt: isoDateSchema,
    completedAt: isoDateSchema.nullable(),
});
export const myJourneysQuerySchema = z.object({
    /** Omit for everything except archived, which is history and asked for explicitly. */
    status: z.enum(['ACTIVE', 'COMPLETED', 'PAUSED', 'ARCHIVED']).optional(),
});
export const myJourneysResponseSchema = z.object({
    items: z.array(userJourneySummarySchema),
    /** So the app can render "Complete or pause a journey to start another" before trying. */
    /**
     * How many journeys are on the go. Informational only — there is no longer a ceiling to
     * compare it against. The cap was removed 2026-10-07; see CLAUDE.md §2.5.
     */
    activeCount: z.number().int(),
});
/* ───────────────────────── starting ───────────────────────── */
export const startJourneyParamsSchema = z.object({ id: objectIdSchema });
/** Starting is idempotent: starting one already started returns it rather than erroring. */
export const startJourneyResponseSchema = userJourneyDetailSchema;
/* ───────────────────────── progress ───────────────────────── */
/**
 * A listening heartbeat, sent every ~10s while playing and again on pause, background and
 * exit.
 *
 * `listenedDeltaSec` is what the client has played SINCE its last heartbeat, not a running
 * total — a total from a client that has been restarted would be wrong, and a delta the
 * server accumulates cannot be inflated by a stale value being resent. The server clamps
 * it, so a client that sleeps and wakes cannot claim a huge jump.
 */
export const progressBodySchema = z.object({
    activityId: objectIdSchema,
    /** Where the playhead is now. Used only for resume. */
    positionSec: z.number().int().min(0),
    /** Seconds genuinely played since the last heartbeat. */
    listenedDeltaSec: z.number().int().min(0).max(120),
    /**
     * The story played to its end. Since 2026-09-14 THIS completes a day and unlocks the next
     * — not 80% listened. A skip or a fast-forward must never stop someone moving on. Listening
     * is still reported, for resume and "minutes today", but it no longer gates anything.
     */
    ended: z.boolean().optional(),
});
/** An achievement that came unlocked as part of this call. */
export const unlockedAchievementSchema = z.object({
    key: z.string(),
    title: z.string(),
    description: z.string(),
    unlockedAt: isoDateSchema,
});
/**
 * What comes back from a heartbeat.
 *
 * Nearly always just the running totals. When the heartbeat is the one that crosses 80%,
 * `dayCompleted` is true and the whole Day Complete screen is in this payload — the streak
 * and tomorrow's title — so the app renders the celebration without a second round trip.
 */
export const progressResponseSchema = z.object({
    dayNumber: z.number().int(),
    status: dayStatusSchema,
    listenedSec: z.number().int(),
    positionSec: z.number().int(),
    /** listenedSec / durationSec, 0-100, so the app does not re-derive the rule. */
    percentListened: z.number().int().min(0).max(100),
    dayCompleted: z.boolean(),
    journeyCompleted: z.boolean(),
    progressPct: z.number().int().min(0).max(100),
    /** Null unless dayCompleted. The user's streak in their own timezone, after this day. */
    streak: z.number().int().nullable(),
    /**
     * True only when THIS completion is the first of the user's local day — the one that
     * moved the streak. A second story the same day reports the same streak with this false,
     * so the app celebrates the day once, not every story (fixed 2026-10-18).
     */
    streakAdvanced: z.boolean(),
    /** Null on the final day — that is what makes it the final day, for the app. */
    nextDayNumber: z.number().int().nullable(),
    nextDayTitle: z.string().nullable(),
    /** Empty unless this call crossed a threshold. */
    achievements: z.array(unlockedAchievementSchema),
});
/* ───────────────────────── playback ───────────────────────── */
export const playbackParamsSchema = z.object({ id: objectIdSchema });
/**
 * The app's playback link — the ONLY endpoint that can return 402, because it is the only
 * place `canAccess` runs. A sub-10-minute story at 128 kbps is about 9 MB, which downloads
 * well inside the 15-minute TTL, so there is no mid-stream refresh to build.
 */
export const appPlaybackResponseSchema = z.object({
    storyId: objectIdSchema,
    activityId: objectIdSchema,
    dayNumber: z.number().int(),
    title: z.string(),
    description: z.string(),
    url: z.string().min(1),
    expiresAt: isoDateSchema,
    durationSec: z.number().int(),
    /**
     * The art behind the player. Signed like the audio and with the same 15-minute life, so
     * it is fetched together with the track rather than resolved a second time.
     */
    bannerUrl: z.string().nullable(),
    /** Resume position, so the player seeks before the first frame plays. */
    positionSec: z.number().int(),
    listenedSec: z.number().int(),
    favourite: z.boolean(),
});
/* ───────────────────────── lifecycle ───────────────────────── */
/**
 * Pause and resume move a journey in and out of the active list. Neither can fail on a
 * count any more — the three-active cap was removed 2026-10-07 — so pausing is now purely
 * a way to set something aside, not a way to free a slot. Removing ARCHIVES: nothing is
 * ever hard-deleted, and the progress stays in history.
 */
export const journeyActionSchema = z.enum(['pause', 'resume', 'archive']);
/* ───────────────────────── catalogue ───────────────────────── */
/**
 * A journey as the LIBRARY sees it — the catalogue plus whatever the user has already done
 * with it, so the All / Active / Completed filters are one call rather than a join done in
 * the app. `userStatus` is null for a journey they have never started.
 *
 * Browsing is deliberately unrestricted: the 3-journey cap is on how many are ACTIVE, and
 * premium journeys are listed like any other. Only playback can be refused.
 */
export const catalogueItemSchema = z.object({
    journey: journeySummarySchema,
    totalDays: z.number().int(),
    userStatus: userJourneyStatusSchema.nullable(),
    progressPct: z.number().int().min(0).max(100),
});
export const catalogueQuerySchema = z.object({
    /** Filter by goal key, as the library's category chips do. */
    goal: z.string().min(1).max(64).optional(),
    /**
     * Drops journeys this user has already COMPLETED.
     *
     * Off by default — browsing the whole catalogue stays unrestricted, and My Journeys has
     * a Completed tab that depends on seeing them. The library turns it on because 4a is
     * "Start a new journey", and a journey already finished is not one you can start.
     *
     * Not `z.coerce.boolean()`: that is `Boolean("false")`, which is true.
     */
    excludeCompleted: z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const catalogueResponseSchema = z.object({
    items: z.array(catalogueItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=journey.js.map